import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SearchIcon from '@material-ui/icons/Search';
import {
  DetailContext,
  FormCustomField,
  FormPanel,
  FormTextField,
  PromptContext,
  TableField,
  TableFieldCells,
  TextField,
  Tooltip,
  useDebouncedCallback,
  useFormSelector,
  usePrompts,
} from '@eas/common-web';
import { useTechnicalObjectsList } from '@modules/technical-object/technical-object-api';
import { TechnicalObjectDetailDialog } from '@modules/technical-object/technical-object-detail-dialog';
import { BRATISLAVA_COORDS_GPS_POINT } from '@composite/map';
import { TechnicalObject, WorkOrder } from '@models';
import { MuzaMapViewMemo } from './muza-map-view';

const applyLocalFilter = (query: string, to: TechnicalObject) => {
  const q = query?.toUpperCase?.();
  return Boolean(
    to?.fid?.toUpperCase?.()?.includes?.(q) ||
      to?.name?.toUpperCase?.()?.includes?.(q) ||
      to?.type?.name?.toUpperCase?.()?.includes?.(q)
  );
};

export function MuzaFields() {
  const { coordinates, woId, woIdentifier, kmLength, isZ105 } = useFormSelector(
    (data: WorkOrder) => ({
      woId: data.id,
      woIdentifier: data.workOrderId,
      coordinates: {
        latitude: data?.latitude ?? BRATISLAVA_COORDS_GPS_POINT.latitude,
        longitude: data?.longitude ?? BRATISLAVA_COORDS_GPS_POINT.longitude,
      },
      woValidCoordinates: data?.latitude && data?.latitude,
      kmLength: data.kmLength?.toFixed(2) ?? '-',
      isZ105: data.workOrderType?.name === 'Z105',
    })
  );

  const [selected, setSelected] = useState<string>();
  const { addRefreshListener, removeRefreshListener } =
    useContext(DetailContext);
  const { testPrompt } = useContext(PromptContext);

  const [technicalObjects, loading] = useTechnicalObjectsList(woId);

  const [toFilterValue, setToFilterValue] = useState('');
  const [toFilter, setToFilter] = useState('');

  const clearFilter = useCallback(() => {
    setToFilter('');
    setToFilterValue('');
    setSelected(undefined);
  }, []);

  useEffect(() => {
    clearFilter();
  }, [clearFilter, woId]);

  useEffect(() => {
    addRefreshListener(clearFilter);
    return () => {
      removeRefreshListener(clearFilter);
    };
  }, [addRefreshListener, removeRefreshListener, clearFilter]);

  const filteredTechnicalObjects = useMemo(
    () => (technicalObjects ?? []).filter((t) => applyLocalFilter(toFilter, t)),
    [technicalObjects, toFilter]
  );

  const [onChangeToFilter] = useDebouncedCallback((val?: string | null) => {
    setToFilter(val ?? '');
  }, 500);

  usePrompts(
    (technicalObjects ?? []).map((to) => ({
      key: `TECHNICAL_OBJECT_DETAIL_DIALOG_${to?.id}`,
      dialogTitle: `${to?.name ?? ''} (${to?.fid ?? ''})`,
      dialogShowConfirm: false,
      dialogWidth: 1200,
      dialogText: (
        <TechnicalObjectDetailDialog
          id={to?.id}
          workOrderId={woId}
          workOrderName={woIdentifier?.toString()}
          isZ105={isZ105}
        />
      ),
    })),
    [technicalObjects]
  );

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ZSD__WORK_ORDERS__MUZA__PANEL__GENERAL"
            defaultMessage="Všeobecné"
          />
        }
      >
        <FormTextField
          name="muzaDescription"
          label={
            <FormattedMessage
              id="ZSD__TECHNICAL_OBJECT__DETAIL__FIELD_LABEL__MUZA_DESCRIPTION"
              defaultMessage="Popis"
            />
          }
          disabled
        />
        {!isZ105 && (
          <FormTextField
            name="revisionUnit"
            label={
              <FormattedMessage
                id="ZSD__TECHNICAL_OBJECT__DETAIL__FIELD_LABEL__MUZA_REVISION_UNIT"
                defaultMessage="Revízny celok"
              />
            }
            disabled
          />
        )}
        <FormTextField
          name="ipid"
          label={
            <FormattedMessage
              id="ZSD__TECHNICAL_OBJECT__DETAIL__FIELD_LABEL__MUZA_IPID"
              defaultMessage="IPID"
            />
          }
          disabled
        />

        {!isZ105 && (
          <>
            <FormTextField
              name="numberOfTechnicalObjects"
              label={
                <FormattedMessage
                  id="ZSD__TECHNICAL_OBJECT__DETAIL__FIELD_LABEL__MUZA_NUM_OF_TO"
                  defaultMessage="Počet technických objektov"
                />
              }
              disabled
            />

            <FormTextField
              name="numberOfPb"
              label={
                <FormattedMessage
                  id="ZSD__TECHNICAL_OBJECT__DETAIL__FIELD_LABEL__MUZA_NUM_OF_PB"
                  defaultMessage="Počet PB"
                />
              }
              disabled
            />
            <FormTextField
              name="workSection"
              label={
                <FormattedMessage
                  id="ZSD__TECHNICAL_OBJECT__DETAIL__FIELD_LABEL__MUZA_WORK_SECTION"
                  defaultMessage="Úsek práce"
                />
              }
              disabled
            />
            <FormTextField
              name="workDirection"
              label={
                <FormattedMessage
                  id="ZSD__TECHNICAL_OBJECT__DETAIL__FIELD_LABEL__MUZA_WORK_DIRECTION"
                  defaultMessage="Odkiaľ - kam"
                />
              }
              disabled
            />
            <FormCustomField
              name="kmLength"
              label={
                <FormattedMessage
                  id="ZSD__TECHNICAL_OBJECT__DETAIL__FIELD_LABEL__MUZA_KM_LENGTH"
                  defaultMessage="Dĺžka v km"
                />
              }
              disabled
            >
              <TextField value={kmLength} disabled />
            </FormCustomField>
            <FormTextField
              name="airPercentage"
              label={
                <FormattedMessage
                  id="ZSD__TECHNICAL_OBJECT__DETAIL__FIELD_LABEL__MUZA_AIR_PERCENTS"
                  defaultMessage="Percento vzduch"
                />
              }
              disabled
            />
            <FormTextField
              name="cablePercentage"
              label={
                <FormattedMessage
                  id="ZSD__TECHNICAL_OBJECT__DETAIL__FIELD_LABEL__MUZA_CABLE_PERCENTS"
                  defaultMessage="Percento kábel"
                />
              }
              disabled
            />
          </>
        )}
      </FormPanel>
      <FormPanel
        label={
          <FormattedMessage
            id="ZSD__WORK_ORDERS__MUZA__PANEL__TECH_OBJECTS_LIST"
            defaultMessage="Zoznam technických objektov"
          />
        }
      >
        {(technicalObjects ?? []).length > 0 && (
          <TextField
            value={toFilterValue}
            startAdornment={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: '0 10px',
                }}
              >
                <SearchIcon fontSize="small" />
                Hľadať technický objekt:
              </div>
            }
            onChange={(val) => {
              setToFilterValue(val ?? '');
              onChangeToFilter(val);
            }}
          />
        )}
        {loading && (
          <div style={{ width: '100%' }}>
            <LinearProgress />
          </div>
        )}

        <TableField
          value={filteredTechnicalObjects ?? []}
          onChange={() => {}}
          disabled={false}
          showDetailBtnCond={() => false}
          showRadioCond={() => false}
          showToolbar={false}
          columns={[
            { datakey: 'name', name: 'Názov', width: 200 },
            { datakey: 'fid', name: 'FID', width: 200 },
            { datakey: 'road', name: 'Cesta', width: 200 },
            { datakey: 'type.name', name: 'Typ', width: 200 },
            {
              datakey: 'id',
              name: 'Detail',
              width: 250,
              CellComponent: (row) => {
                const noCoordinates =
                  (row.rowValue?.coordinates ?? []).length === 0;
                const hiddenOnMap = row.rowValue?.type?.hiddenOnMap;
                return (
                  <div>
                    <Tooltip title="Otvoriť detail objektu v popup okne">
                      <Button
                        variant="text"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          testPrompt({
                            key: `TECHNICAL_OBJECT_DETAIL_DIALOG_${row?.value}`,
                            submitCallback: () => {},
                          });
                        }}
                      >
                        <OpenInBrowserIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Otvoriť detail objektu v novom tabe">
                      <Button
                        variant="text"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          window.open(
                            `/zsd/technicke-objekty/${row?.value}?pracovnyPrikazId=${woId}&pracovnyPrikaz=${woIdentifier}&z105=${isZ105}`,
                            '_blank'
                          );
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                    {!hiddenOnMap && (
                      <Tooltip
                        title={`${
                          noCoordinates
                            ? 'Technický objekt neobsahuje platné súradnice'
                            : 'Zobraziť na mape'
                        }`}
                      >
                        <Button
                          disabled={noCoordinates}
                          variant="text"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const c = row.rowValue?.coordinates?.[0];
                            if (c) {
                              setSelected(row.rowValue.fid);
                            }
                          }}
                        >
                          <CenterFocusStrongIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                );
              },
            },
            {
              datakey: 'hasDefect',
              name: 'Obsahuje nedostatok',
              width: 150,
              CellComponent: TableFieldCells.BooleanCell,
            },
            {
              datakey: 'visited',
              name: 'Navštívenosť',
              width: 150,
              CellComponent: TableFieldCells.BooleanCell,
            },
          ]}
        />
      </FormPanel>
      <MuzaMapViewMemo
        selected={selected}
        setSelected={setSelected}
        technicalObjects={(technicalObjects ?? []).filter(
          (t) => t.type?.hiddenOnMap !== true
        )}
        woId={woId}
        woCoords={coordinates}
        woIdentifier={woIdentifier}
        loading={loading}
      />
    </>
  );
}
